// src/modules/home/components/slots/RightPanelShell.jsx
// ─────────────────────────────────────────────────────────────────────────────
// RIGHT PANEL SHELL — Slot-based right panel orchestrator
//
// This is the new RIGHT PANEL architecture. Instead of one monolithic component,
// it renders "slots" from the SlotRegistry + built-in tab sections.
//
// LAYOUT:
//   ┌──────────────────────────────┐
//   │  Tab bar: DAG | SAVED | GUIDE│   (always present when authenticated)
//   ├──────────────────────────────┤
//   │  Active tab content          │   (DAGViewer / Pipeline / Guide)
//   ├──────────────────────────────┤
//   │  Context slot                │   (module-specific saved items)
//   ├──────────────────────────────┤
//   │  Dynamic slots from registry │   (anything modules register)
//   └──────────────────────────────┘
//
// WHY TABS + SLOTS:
//   Tabs = the 3 core workflow views (always there, predictable UX)
//   Slots = extensible sections below/between (adaptable, can add/remove)
//
// ADDING A NEW SECTION:
//   1. Create a slot component in src/modules/home/components/slots/
//   2. Register it in initRightPanelSlots() below
//   3. Done. It appears in the panel when its condition is met.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useEffect, useCallback } from 'react';
import { T } from '../TopBar';
import { loadSession, loadPipelines, getModuleInfo, getUserKey } from '../../../utils/actionStore';

import DAGViewerSlot from './DAGViewerSlot';
import PipelineSlot from './PipelineSlot';
import MemoryCookieSlot from './MemoryCookieSlot';
import AboutJorvexSlot from './AboutJorvexSlot';

const MONO = 'JetBrains Mono,monospace';

// ── Tab definitions ─────────────────────────────────────────────────────────
const TABS = [
  { id: 'memory', label: 'MEMORY', icon: '🧠' },
  { id: 'dag',    label: 'DAG',    icon: '⬡'  },
  { id: 'about',  label: 'ABOUT',  icon: '✦'  },
];

// ── MAIN SHELL ──────────────────────────────────────────────────────────────
export default function RightPanelShell({ activeModule, authUser, onNavigate }) {
  const userKey = getUserKey(authUser);
  const [session, setSession] = useState(() => loadSession(userKey));
  const [pipelines, setPipelines] = useState(() => loadPipelines(userKey));
  const [activeTab, setActiveTab] = useState('memory');
  const [dagTab, setDagTab] = useState('live');

  const moduleInfo = getModuleInfo(activeModule);
  const color = moduleInfo.color;
  const moduleNodeCount = (session.nodes || []).filter((n) => n.module === activeModule).length;
  const modulePipelineCount = pipelines.filter((p) => p.module === activeModule || !p.module).length;

  // Listen for DAG updates
  useEffect(() => {
    const handler = () => {
      setSession(loadSession(userKey));
      setPipelines(loadPipelines(userKey));
    };
    window.addEventListener('dd:dag:updated', handler);
    return () => window.removeEventListener('dd:dag:updated', handler);
  }, [userKey]);

  useEffect(() => {
    setDagTab('live');
  }, [activeModule]);

  const handleSessionChange = useCallback(() => {
    setSession(loadSession(userKey));
  }, [userKey]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>

      {/* ─── Accent strip ─── */}
      <div style={{ height: 2, background: color, borderRadius: '2px 2px 0 0' }} />

      {/* ─── Tab row — flat, no box ─── */}
      <div style={{ display: 'flex', gap: 0, borderBottom: `1px solid ${T.b1}` }}>
        {TABS.map(tab => {
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                flex: 1, padding: '7px 0',
                border: 'none',
                borderBottom: isActive ? `2px solid ${color}` : '2px solid transparent',
                background: 'transparent',
                color: isActive ? color : T.t4,
                fontSize: 8, fontFamily: MONO, fontWeight: 700,
                cursor: 'pointer', letterSpacing: '.08em',
                transition: 'color .12s, border-color .12s',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
              }}
            >
              <span style={{ fontSize: 9 }}>{tab.icon}</span>
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* ─── Tab content — flat, no box ─── */}
      <div style={{ padding: activeTab === 'dag' ? '8px 6px' : activeTab === 'memory' ? '0' : '10px 12px' }}>

        {/* ── BETA notice ── */}
        <div style={{
          margin: '10px 10px 6px',
          padding: '9px 12px',
          background: '#fbbf2410',
          border: '1px solid #fbbf2430',
          borderLeft: '3px solid #fbbf24bb',
          borderRadius: 3,
          display: 'flex', alignItems: 'flex-start', gap: 8,
        }}>
          <span style={{ fontSize: 13, flexShrink: 0, lineHeight: 1 }}>🚧</span>
          <div>
            <div style={{ fontSize: 9, fontFamily: MONO, fontWeight: 800, color: '#fbbf24cc', letterSpacing: '.1em', marginBottom: 3 }}>
              RIGHT PANEL — BETA
            </div>
            <div style={{ fontSize: 8, fontFamily: MONO, color: T.t3, lineHeight: 1.55 }}>
              This section is currently being tested. Features may change, move, or behave unexpectedly.
            </div>
          </div>
        </div>
        {activeTab === 'memory' && <MemoryCookieSlot />}
        {activeTab === 'dag' && (
          <>
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 6,
              margin: '0 4px 10px',
              padding: 4,
              background: T.void,
              border: `1px solid ${T.b0}`,
              borderRadius: 4,
            }}>
              {[
                { id: 'live', label: 'LIVE', count: moduleNodeCount },
                { id: 'saved', label: 'SAVED', count: modulePipelineCount },
              ].map((tab) => {
                const isActive = dagTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setDagTab(tab.id)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 8,
                      padding: '8px 10px',
                      borderRadius: 3,
                      border: `1px solid ${isActive ? `${color}55` : T.b0}`,
                      background: isActive ? `${color}12` : 'transparent',
                      color: isActive ? color : T.t4,
                      cursor: 'pointer',
                      fontFamily: MONO,
                      fontSize: 8,
                      fontWeight: 800,
                      letterSpacing: '.12em',
                    }}
                  >
                    <span>{tab.label}</span>
                    <span style={{
                      minWidth: 18,
                      padding: '1px 4px',
                      borderRadius: 999,
                      background: isActive ? `${color}20` : T.surface,
                      color: isActive ? color : T.t3,
                      textAlign: 'center',
                      fontSize: 7,
                    }}>
                      {tab.count}
                    </span>
                  </button>
                );
              })}
            </div>

            {dagTab === 'live' ? (
              <DAGViewerSlot
                activeModule={activeModule}
                authUser={authUser}
                session={session}
                onSessionChange={handleSessionChange}
              />
            ) : (
              <PipelineSlot
                activeModule={activeModule}
                authUser={authUser}
                color={color}
              />
            )}
          </>
        )}
        {activeTab === 'about' && <AboutJorvexSlot activeModule={activeModule} />}
      </div>
    </div>
  );
}
